import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import { Spacing4 } from 'autogen/design-tokens/tokens';
import { PlayingInXSeconds } from 'autogen/translation-keys/trans-website-product';
import { ButtonWithIcon } from 'components/Button';
import Card from 'components/generic/card';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import { format } from 'modules/helpers/strings';
import { useRecoilValue } from 'recoil';
import useVideoDetails, { type VideoDetailsProps } from '../hooks/useVideoDetails';
import { videoProgressState } from '../recoil/atoms';

/* #region styles  */
const StyledCard = styled(Card)`
    flex-direction: row;
`;

const StyledCardContent = styled(Card.Content)`
    align-items: flex-start;
    position: relative;
`;

const StyledLabel = styled(Typography.Label)`
    opacity: 0.6;
    margin-bottom: ${Spacing4};
`;

const Title = styled(Typography.Body)`
    margin-bottom: ${Spacing4};
`;

const Description = styled(Typography.Body)`
    margin: 0;
`;

const ImageContainer = styled.div`
    position: relative;
    width: 80px;
    height: 80px;
`;

const StyledImg = styled.img`
    object-fit: cover;
    object-position: center;
    min-width: 80px;
    min-height: 80px;
    max-width: 100%;
    max-height: 100%;
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    position: absolute;
    width: 24px;
    height: 24px;
    padding: 8px;
    right: 4px;
    top: 4px;
`;
/* #endregion */

const Container = styled.div`
    position: absolute;
    padding: 0 12px;
    width: 100%;
    left: 50%;
    bottom: 12px;
    max-width: 375px;
`;

// encapsulate state changes to avoid rerender
function CountdownDescription() {
    const { t } = useTranslation();
    const { timeLeftSeconds } = useRecoilValue(videoProgressState);
    return <Description size="S">{format(t(PlayingInXSeconds))(timeLeftSeconds)}</Description>;
}

export function AutoplayCard({ video, dismiss }) {
    const { thumbnail, title, duration } = video;

    return (
        <StyledCard>
            <ImageContainer>
                <StyledImg alt="thumbnail" src={thumbnail} />
            </ImageContainer>
            <StyledCardContent>
                <StyledLabel size="XS">{duration}</StyledLabel>
                <Title size="S" strong>
                    {title}
                </Title>
                <CountdownDescription />
                <StyledButtonWithIcon
                    size="sm"
                    variant="ghostOnLight"
                    icon={<Icon type="Close" size={16} />}
                    onClick={dismiss}
                />
            </StyledCardContent>
        </StyledCard>
    );
}

const AnimatedContainer = animated<any>(Container);

const AutoplayCardWrapper = memo<{
    video: VideoDetailsProps;
    isWithinTargetEndTime: boolean;
}>(({ video, isWithinTargetEndTime }) => {
    const productVideo = useVideoDetails(video);
    const [isCardVisible, setIsCardVisible] = useState(true);

    const shouldDisplay = isWithinTargetEndTime && productVideo && isCardVisible;

    const transition = useTransition(shouldDisplay, {
        from: { y: 20, x: '-50%', opacity: 0 },
        enter: { y: 0, x: '-50%', opacity: 1 },
        leave: { y: 20, x: '-50%', opacity: 0 }
    });

    return transition(
        (styles, item) =>
            item && (
                <AnimatedContainer
                    style={styles}
                    onClick={(event) => {
                        event.stopPropagation(); // remove this when full react and not mounted by module loader
                    }}
                >
                    <AutoplayCard video={productVideo} dismiss={() => setIsCardVisible(false)} />
                </AnimatedContainer>
            )
    );
});

// We have this wrapper component to avoid rerendering Autoplaycard every second
export default function CalculateTimeForAutoplayCard({ video, targetEndTimeSeconds = 6 }) {
    const { timeLeftSeconds } = useRecoilValue(videoProgressState);

    const isWithinTargetEndTime = timeLeftSeconds !== 0 && timeLeftSeconds <= targetEndTimeSeconds;

    return <AutoplayCardWrapper video={video} isWithinTargetEndTime={isWithinTargetEndTime} />;
}
